/**
 * @fileoverview `<SearchInput>` — champ de recherche standard GNPC.
 *
 * Les listes backoffice partagent le même besoin : recherche textuelle dense,
 * icône loupe et bouton d'effacement optionnel. Ce wrapper évite de répéter
 * le même assemblage `<Input leftIcon={<Search />}>` partout.
 */

import * as React from 'react';
import { Search, X } from 'lucide-react';
import { Button } from './ui/button.tsx';
import { Input, type InputProps } from './ui/input.tsx';
import { cn } from '../lib/utils.ts';

export interface SearchInputProps extends Omit<InputProps, 'leftIcon' | 'type'> {
  /** Callback déclenché par le bouton d'effacement. */
  onClear?: () => void;
  /** Libellé accessible du bouton d'effacement. */
  clearLabel?: string;
}

/**
 * Champ de recherche avec icône et bouton clear.
 *
 * @param props - Props d'input GNPC et callback d'effacement.
 * @returns Input de recherche prêt pour les toolbars DataTable.
 *
 * @example
 * ```tsx
 * <SearchInput value={query} onChange={onQueryChange} onClear={() => setQuery('')} />
 * ```
 */
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  { className, value, onClear, clearLabel = 'Effacer la recherche', placeholder = 'Rechercher…', ...props },
  ref,
) {
  const hasValue = typeof value === 'string' && value.length > 0;

  return (
    <div className="relative w-full">
      <Input
        ref={ref}
        type="search"
        leftIcon={<Search />}
        value={value}
        placeholder={placeholder}
        className={cn(onClear && hasValue && 'pr-10', className)}
        {...props}
      />
      {onClear && hasValue ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          className="absolute right-2 top-1/2 -translate-y-1/2"
          onClick={onClear}
          aria-label={clearLabel}
        >
          <X />
        </Button>
      ) : null}
    </div>
  );
});
